import { ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { Capacitor } from "@capacitor/core";

const PRIMARY_WEB_HOSTS = new Set(["timely365.com", "www.timely365.com"]);
const PRIMARY_GRAPHQL_URI = "https://timely365.com/graphql";

function normalizeGraphqlUri(uri) {
  if (!uri) return uri;
  try {
    const isRelative = !/^[a-z]+:\/\//i.test(uri);
    const parsed = new URL(uri, "http://localhost");
    const pathname = parsed.pathname || "";

    if (!/\/graphql\/?$/i.test(pathname)) {
      parsed.pathname = `${pathname.replace(/\/+$/, "")}/graphql`;
    }

    return isRelative
      ? `${parsed.pathname}${parsed.search}${parsed.hash}`
      : parsed.toString();
  } catch {
    return uri;
  }
}

function rewriteLocalhostForAndroid(uri) {
  try {
    const parsed = new URL(uri, "http://localhost");
    const host = parsed.hostname?.toLowerCase();
    const isLocal =
      host === "localhost" || host === "127.0.0.1" || host === "::1";

    if (isLocal) {
      parsed.hostname = "10.0.2.2";
    }

    const isRelative = !/^[a-z]+:\/\//i.test(uri);
    return isRelative ? uri : parsed.toString();
  } catch {
    return uri;
  }
}

function isRelativeUri(uri) {
  return typeof uri === "string" && !/^[a-z]+:\/\//i.test(uri);
}

function shouldForcePrimaryGraphqlHost() {
  if (typeof window === "undefined") return false;
  const hostname = String(window.location.hostname || "").trim().toLowerCase();
  if (!hostname) return false;
  return !PRIMARY_WEB_HOSTS.has(hostname);
}

export const getApiServerUri = () => {
  const platform = Capacitor.getPlatform();
  const isDev = process.env.NODE_ENV === "development";
  const envUri = process.env.REACT_APP_API_SERVER_URI;
  const isWeb = platform === "web";

  if (envUri) {
    const normalizedEnvUri = normalizeGraphqlUri(envUri);
    if (isDev && platform === "android") {
      return rewriteLocalhostForAndroid(normalizedEnvUri);
    }
    if (!isDev && isWeb && isRelativeUri(normalizedEnvUri) && shouldForcePrimaryGraphqlHost()) {
      return PRIMARY_GRAPHQL_URI;
    }
    return normalizedEnvUri;
  }

  if (isDev) {
    if (platform === "android") return "http://10.0.2.2:4000/graphql";
    if (platform === "ios") return "http://localhost:4000/graphql";
    return "http://localhost:4000/graphql";
  }

  if (!isDev && isWeb) {
    return PRIMARY_GRAPHQL_URI;
  }

  return normalizeGraphqlUri(process.env.REACT_APP_API_SERVER_URI);
};

let _preferences = null;
async function getPreferences() {
  if (_preferences) return _preferences;
  try {
    if (Capacitor.getPlatform() !== "web") {
      const mod = await import("@capacitor/preferences");
      _preferences = mod.Preferences;
      return _preferences;
    }
  } catch {
  }
  return null;
}

async function getAuthToken() {
  if (Capacitor.getPlatform() !== "web") {
    const Preferences = await getPreferences();
    if (Preferences) {
      try {
        const { value } = await Preferences.get({ key: "token" });
        return value ?? null;
      } catch {
      }
    }
  }

  try {
    const sessionToken = sessionStorage.getItem("token");
    if (sessionToken && sessionToken !== "undefined") return sessionToken;

    const legacyToken = localStorage.getItem("token");
    if (legacyToken && legacyToken !== "undefined") {
      sessionStorage.setItem("token", legacyToken);
      return legacyToken;
    }
    return null;
  } catch {
    return null;
  }
}

const uri = getApiServerUri();
const httpLink = createHttpLink({ uri });

const authLink = setContext(async (_, { headers }) => {
  const token = await getAuthToken();
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
});

export default client;
